import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { dig, isPresent } from '@imcc/utilities';
import { WINDOW } from '@lib/browser/window-providers';
import { waitForMedia } from '@lib/dom/wait-for-media';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScrollHistoryMap } from './scroll-history-map';
import { ScrollSnapshot } from './scroll-snapshot';

@Injectable()
export class ScrollRestorationService {
  scrollPositionHistory$: BehaviorSubject<
  ScrollHistoryMap
  > = new BehaviorSubject({});

  saveScrollHistory$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationStart),
  );

  restoreScroll$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    waitForMedia(),
  );

  constructor(
    private router: Router,
    @Inject(WINDOW) private windowRef: Window,
  ) {}

  public enable = () => {
    this.saveScrollHistory$.subscribe(() => {
      this.saveScrollHistory();
    });

    this.restoreScroll$.subscribe(() => {
      const url = this.getCurrentUrl();
      const scrollHistory = this.scrollPositionHistory$.value[url] || new ScrollSnapshot();

      return this.restoreScroll(scrollHistory);
    });
  };

  private saveScrollHistory() {
    const currentNavigation = this.router.getCurrentNavigation();
    const restorationPath = ['extras', 'state', 'scrollRestoration'];
    const restoration = dig(currentNavigation, ...restorationPath);
    const url = this.getCurrentUrl();

    if (isPresent(restoration)) {
      const previousHistory = this.scrollPositionHistory$.value;
      const scrollHistory: ScrollSnapshot = {
        restoration,
        position: { x: this.windowRef.scrollX, y: this.windowRef.scrollY },
      };
      const scrollHistoryMap: ScrollHistoryMap = {
        ...previousHistory,
        [url]: scrollHistory,
      };

      this.scrollPositionHistory$.next(scrollHistoryMap);
    }
  }

  private restoreScroll(scrollHistory: ScrollSnapshot) {
    const { restoration, position } = scrollHistory;

    switch (restoration) {
      case 'maintain':
        this.windowRef.scrollTo(position.x, position.y);
        break;
      case 'frameworkDefault':
      default:
    }
  }

  private getCurrentUrl = () => {
    const { routerState } = this.router;
    const urlPath = ['snapshot', 'url'];

    return dig(routerState, ...urlPath);
  };
}
